import { useCallback, useRef } from 'react';

import type { RedactionScope } from '../types';
import { useRedaction } from '../transcription/useRedaction';

import type { FieldKey } from './FieldProposalReviewModal.types';

type UseFieldRedactionOptions = {
   field: FieldKey;
   getText: () => string;
   onRedacted: (text: string) => void;
};

export type UseFieldRedactionResult = ReturnType<typeof useRedaction> & {
   textareaRef: React.RefObject<HTMLTextAreaElement>;
};

export function useFieldRedaction(options: UseFieldRedactionOptions): UseFieldRedactionResult {
   const textareaRef = useRef<HTMLTextAreaElement>(null);

   const getSelectionRange = useCallback(() => {
      const el = textareaRef.current;
      if (!el) {
         return null;
      }
      const start = el.selectionStart;
      const end = el.selectionEnd;
      if (start === end) {
         return null;
      }
      return { start: start, end: end, text: el.value.slice(start, end) };
   }, []);

   const redaction = useRedaction({
      field: () => options.field,
      getFieldText: options.getText,
      getSelectionRange: getSelectionRange,
      onAccept: (redactedText: string, scope: RedactionScope) => {
         if (scope.kind === 'selection') {
            const full = options.getText();
            options.onRedacted(full.slice(0, scope.selectionStart) + redactedText + full.slice(scope.selectionEnd));
         } else {
            options.onRedacted(redactedText);
         }
      },
   });

   return { ...redaction, textareaRef: textareaRef };
}
